import type { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { z } from 'zod';

export type TRequestSignatureModalStep =
  | 'select-document'
  | 'select-recipients'
  | 'sender-view';

export const recipientSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  email: z.string().trim().email('Enter a valid email address'),
});

export const recipientsSchema = z
  .array(recipientSchema)
  .min(1, 'Add at least one recipient');

/** Form schema for RHF: root object with recipients array */
export const recipientsFormSchema = z.object({
  recipients: recipientsSchema,
});

export type RecipientInput = z.input<typeof recipientSchema>;
/** Parsed output: strings are trimmed and validated. Use for API payloads. */
export type RecipientValidated = { name: string; email: string };
export type RecipientsFormValues = z.infer<typeof recipientsFormSchema>;
export type RecipientsValidated = z.output<typeof recipientsSchema>;

export const GENERIC_ERROR =
  'Something went wrong. Please try again or contact Commenda support.';

export type TBaseProps = {
  isOpen: boolean;
  onClose: () => void;
};

export type TServiceRequestMode = TBaseProps & {
  mode: 'service-request';
  serviceRequest: ServiceRequestDetails;
  companyId?: number | null;
  onFilesRefetch?: () => void;
};

export type TAgentToolMode = TBaseProps & {
  mode: 'agent-tool';
  agentFirmId: string;
  onSuccess?: () => Promise<void> | void;
};

/** An ad-hoc HUMAN_ESIGN task: same flow as the matter mode, bound to a task and opened on a resolved document. */
export type TWorkflowTaskMode = TBaseProps & {
  mode: 'workflow-task';
  serviceRequest: ServiceRequestDetails;
  companyId?: number | null;
  serviceRequestTaskId: number;
  /** The upstream step's generated document, preselected. The assignee can still pick or upload another. */
  presetFileId?: number;
  onFilesRefetch?: () => void;
  onSuccess?: () => Promise<void> | void;
};

export type RequestSignatureModalProps =
  | TServiceRequestMode
  | TAgentToolMode
  | TWorkflowTaskMode;
